// ABOUTME: Hook that collects the edits made by manual-save Setting components and writes
// ABOUTME: them all at once, for settings screens that persist through one Save button.

import { useCallback, useState } from 'react';
import { useTranslation } from "react-i18next";
import { useUpdateSettingForRelationMutation, useUpdateSettingMutation } from '../../../newapi/global/generalApiSlice';
import { showToastMessage, showToastMessageRequestError } from '../../../utils/toasts';

// Stored values arrive as strings while the inputs hand back numbers, booleans or ids,
// so an edit counts as a change on its rendered value rather than on its type.
const isSameValue = (value, storedValue) => {
  if (Array.isArray(value)) {
    return JSON.stringify(value) === JSON.stringify(storedValue ?? []);
  }
  return String(value ?? "") === String(storedValue ?? "");
};

// Every Setting component rendered with `manualSave` reports its edits here through
// `onChange(value, setting)` instead of persisting them itself. `save` writes the ones
// that still differ from what is stored; nothing is written while `hasChanges` is false.
const useManualSettingsSave = () => {
  const { t } = useTranslation();
  const [updateSetting] = useUpdateSettingMutation();
  const [updateSettingForRelation] = useUpdateSettingForRelationMutation();

  const [pending, setPending] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const handleChange = useCallback((value, setting) => {
    if (!setting?.key) {
      return;
    }
    setPending((prev) => {
      const next = { ...prev };
      const storedValue = Array.isArray(value) ? setting.objectBody : setting.value;
      if (isSameValue(value, storedValue)) {
        delete next[setting.key];
      } else {
        next[setting.key] = { setting, value };
      }
      return next;
    });
  }, []);

  const hasChanges = Object.keys(pending).length > 0;

  const save = useCallback(async () => {
    const edits = Object.values(pending);
    if (isSaving || edits.length === 0) {
      return;
    }
    setIsSaving(true);
    try {
      for (const { setting, value } of edits) {
        // A multi-select carries its selection in objectBody, the rest in value.
        const body = Array.isArray(value)
          ? { key: setting.key, objectBody: value, value: "" }
          : { key: setting.key, value: value };

        if (setting.relationId) {
          await updateSettingForRelation({ ...body, relationId: setting.relationId }).unwrap();
        } else {
          await updateSetting({ ...body, warehouseId: setting.warehouseId }).unwrap();
        }
      }
      setPending({});
      showToastMessage("success", t("update_successfull"));
    } catch (error) {
      // The pending edits are kept so the screen still shows what failed to save.
      showToastMessageRequestError(t("update_failed"), t, error);
    } finally {
      setIsSaving(false);
    }
  }, [pending, isSaving, updateSetting, updateSettingForRelation, t]);

  return { handleChange, hasChanges, isSaving, save };
};

export default useManualSettingsSave;
